import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from './Modal';

interface CancelOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCancel: (reason: string) => void;
  loading?: boolean;
}

const CancelOrderModal: React.FC<CancelOrderModalProps> = ({
  isOpen,
  onClose,
  onCancel,
  loading = false
}) => {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (reason.trim()) {
      onCancel(reason.trim());
    }
  };

  const handleClose = () => {
    setReason('');
    onClose();
  };

  const handleQuickTextClick = (text: string) => {
    setReason(text);
  };

  const quickTexts = [
    t('modals.cancelOrder.quickText1'),
    t('modals.cancelOrder.quickText2'),
    t('modals.cancelOrder.quickText3'),
    t('modals.cancelOrder.quickText4'),
    t('modals.cancelOrder.quickText5'),
  ];

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={t('modals.cancelOrder.title')}>
      <form onSubmit={handleSubmit} className="modal-form">
        <div className="modal-form-group">
          <label htmlFor="reason">{t('modals.cancelOrder.reasonLabel')}</label>

          {/* Quick Texts */}
          <div style={{ marginBottom: '0.75rem' }}>
            <small style={{ display: 'block', marginBottom: '0.5rem', color: '#666', fontWeight: 500 }}>
              {t('modals.cancelOrder.quickTexts')}
            </small>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {quickTexts.map((text, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => handleQuickTextClick(text)}
                  disabled={loading}
                  style={{
                    padding: '0.5rem 0.75rem',
                    fontSize: '0.85rem',
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease',
                    color: '#495057',
                    opacity: loading ? 0.6 : 1,
                  }}
                  onMouseEnter={(e) => {
                    if (!loading) {
                      e.currentTarget.style.backgroundColor = '#e9ecef';
                      e.currentTarget.style.borderColor = '#adb5bd';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!loading) {
                      e.currentTarget.style.backgroundColor = '#f8f9fa';
                      e.currentTarget.style.borderColor = '#dee2e6';
                    }
                  }}
                >
                  {text}
                </button>
              ))}
            </div>
          </div>

          <textarea
            id="reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('modals.cancelOrder.reasonPlaceholder')}
            required
            disabled={loading}
          />
        </div>

        <div className="modal-actions">
          <button
            type="button"
            onClick={handleClose}
            className="modal-button secondary"
            disabled={loading}
          >
            {t('modals.cancelOrder.cancel')}
          </button>
          <button
            type="submit"
            className="modal-button danger"
            disabled={loading || !reason.trim()}
          >
            {loading ? t('modals.cancelOrder.cancelling') : t('modals.cancelOrder.cancelOrder')}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default CancelOrderModal;
